import { useRef, useCallback } from "react";
import { Loader, PostCard } from "@/components/shared";
import { useGetRecentPosts } from "@/lib/react-query/queries";
import SEOComponent from "@/components/shared/SEOComponent";
import { useOutletContext } from "react-router-dom";

interface OutletContextType {
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
}

const Sellers = () => {
  const { selectedCategory } = useOutletContext<OutletContextType>();

  // ✅ FIXED: Pass all 3 parameters correctly
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetRecentPosts(
    selectedCategory,  // category
    "sell-something",  // individual  
    true              // isActivated
  );

  const observerRef = useRef<IntersectionObserver | null>(null);

  const lastPostRef = useCallback(
    (node: HTMLElement | null) => {
      if (isFetchingNextPage) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  );

  if (isError) {
    return (
      <div className="flex flex-1">
        <div className="home-container">
          <p className="body-medium text-light-1">Something went wrong</p>
        </div>
      </div>
    );
  }

  // ✅ No need to filter again - the query already filters
  const allPosts = data?.pages?.flatMap((page) => page.documents) ?? [];

  const showNoResults = !isLoading && allPosts.length === 0;
  const showEndMessage = !hasNextPage && !isLoading && allPosts.length > 0;

  return (
    <>
      <SEOComponent
        title="BUSICONN - Sellers"
        description="This page contains the details of all the posts from verified sellers offering physical goods, services, and digital products."
        canonicalUrl="https://busiconn.com/sellers"
      />
      
      <div className="flex flex-1">
        <div className="home-container">
          <h2 className="h3-bold md:h2-bold text-left w-full">Sellers Feed</h2>

          {/* Loading State */}
          {isLoading && !data ? (
            <Loader />
          ) : (
            <>
              {/* Posts List */}
              <ul className="flex flex-col flex-1 gap-6 w-full">
                {allPosts.map((post, index) => (
                  <li
                    key={post.$id}
                    ref={index === allPosts.length - 1 ? lastPostRef : null}
                    className="flex justify-center w-full"
                  >
                    <PostCard post={post} showDealBadge={true} />
                  </li>
                ))}
              </ul>

              {/* Loading More Indicator */}
              {isFetchingNextPage && <Loader />}

              {/* No Results */}
              {showNoResults && (
                <div className="flex-center flex-col gap-4 mt-10">
                  <img 
                    src="/assets/icons/no-deals.svg" 
                    alt="No sellers"
                    className="h-24 w-24"
                  />
                  <p className="text-light-3 text-center">
                    No seller posts available yet
                  </p>
                </div>
              )}

              {/* End of Results */}
              {showEndMessage && (
                <p className="text-center text-light-3 mt-4">You've reached the end</p>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Sellers;